import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessage, defineMessages } from 'react-intl'
import Places from 'constants/places'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import switchFunc from 'lib/function/switchFunc'

export const EXPERIENCE_PRODUCT_TYPE_ALWAYS_ON = 'always_on'
export const EXPERIENCE_PRODUCT_TYPE_FLASH = 'flash'
export const EXPERIENCE_PRODUCT_TYPE_CHANNEL_MANAGER = 'channel_manager'
export const EXPERIENCE_PRODUCT_TYPE_TRANSFER = 'transfer'
export const EXPERIENCE_PRODUCT_TYPES: Array<Exclude<App.ExperienceProductType, 'addon' | 'transfer'>> = [EXPERIENCE_PRODUCT_TYPE_ALWAYS_ON, EXPERIENCE_PRODUCT_TYPE_FLASH, EXPERIENCE_PRODUCT_TYPE_CHANNEL_MANAGER]
export const EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW = 5
export const EXPERIENCE_GIFT_SCHEDULE_DATE_LIMIT_BEFORE_EXPIRATION_DATE = 14
export const EXPERIENCE_ANY_DATE = 'any'
export const EXPERIENCE_TRANSFER_ID = 36
export const EXPERIENCE_CANCELLATION_REASON_DOWNGRADED = 'downgraded'

/**
 * Localised labels for curated experience search destinations.
 * Stable English values are derived from defaultMessage for search matching and analytics.
 */
export const experienceDestinationMessages = defineMessages({
  melbourne: {
    id: 'mbTATho5gnH-36CstNmt',
    defaultMessage: 'Melbourne',
    description: 'Curated experience search destination label for Melbourne shown in trending chips and search placeholders',
  },
  sydney: {
    id: 'WLEf6OZXaZZ05Rlcbf8b',
    defaultMessage: 'Sydney',
    description: 'Curated experience search destination label for Sydney shown in trending chips and search placeholders',
  },
  brisbane: {
    id: 'f0hSZF1cBxQwpV2L7uqY',
    defaultMessage: 'Brisbane',
    description: 'Curated experience search destination label for Brisbane shown in trending chips and search placeholders',
  },
  goldCoast: {
    id: '2bF6JsCE77cl8jma9Xc1',
    defaultMessage: 'Gold Coast',
    description: 'Curated experience search destination label for the Gold Coast shown in trending chips and search placeholders',
  },
  adelaide: {
    id: '6o72zg7V__Vxioo6jN5r',
    defaultMessage: 'Adelaide',
    description: 'Curated experience search destination label for Adelaide shown in trending chips and search placeholders',
  },
  perth: {
    id: 'lCExrstMEgLVoiYI-RE9',
    defaultMessage: 'Perth',
    description: 'Curated experience search destination label for Perth shown in trending chips and search placeholders',
  },
  thailand: {
    id: 'enqsWdkwhMQHdD_Jo5iv',
    defaultMessage: 'Thailand',
    description: 'Curated experience search destination label for Thailand shown in trending chips and search placeholders',
  },
  bali: {
    id: 'mvuflueWILR4WGm7iOCh',
    defaultMessage: 'Bali',
    description: 'Curated experience search destination label for Bali shown in trending chips and search placeholders',
  },
  anywhere: {
    id: 'yMnF6e3iazP9YQuxDqDS',
    defaultMessage: 'Anywhere',
    description: 'Curated experience search destination label for the open-ended Anywhere option in experience search',
  },
  newZealand: {
    id: 'CVPyW9Wm7h-TGngmvT2G',
    defaultMessage: 'New Zealand',
    description: 'Curated experience search destination label for New Zealand shown in trending chips and search placeholders',
  },
  fiji: {
    id: 'wlwn4AHNkeSeoZFcxnCk',
    defaultMessage: 'Fiji',
    description: 'Curated experience search destination label for Fiji shown in trending chips and search placeholders',
  },
  auckland: {
    id: 'vRpVDe4bMMhW0XlAueFM',
    defaultMessage: 'Auckland',
    description: 'Curated experience search destination label for Auckland shown in trending chips and search placeholders',
  },
  christchurch: {
    id: 'W75kKNrnjVKL2SM29Fh1',
    defaultMessage: 'Christchurch',
    description: 'Curated experience search destination label for Christchurch shown in trending chips and search placeholders',
  },
  queenstown: {
    id: '3FepkWIry8j85050li4t',
    defaultMessage: 'Queenstown',
    description: 'Curated experience search destination label for Queenstown shown in trending chips and search placeholders',
  },
})

export const EXPERIENCE_DESTINATION = {
  MELBOURNE: experienceDestinationMessages.melbourne.defaultMessage,
  SYDNEY: experienceDestinationMessages.sydney.defaultMessage,
  BRISBANE: experienceDestinationMessages.brisbane.defaultMessage,
  GOLD_COAST: experienceDestinationMessages.goldCoast.defaultMessage,
  ADELAIDE: experienceDestinationMessages.adelaide.defaultMessage,
  PERTH: experienceDestinationMessages.perth.defaultMessage,
  THAILAND: experienceDestinationMessages.thailand.defaultMessage,
  BALI: experienceDestinationMessages.bali.defaultMessage,
  ANYWHERE: experienceDestinationMessages.anywhere.defaultMessage,
  NEW_ZEALAND: experienceDestinationMessages.newZealand.defaultMessage,
  FIJI: experienceDestinationMessages.fiji.defaultMessage,
  AUCKLAND: experienceDestinationMessages.auckland.defaultMessage,
  CHRISTCHURCH: experienceDestinationMessages.christchurch.defaultMessage,
  QUEENSTOWN: experienceDestinationMessages.queenstown.defaultMessage,
} as const

export type ExperienceDestination = typeof EXPERIENCE_DESTINATION[keyof typeof EXPERIENCE_DESTINATION]

const experienceDestinationMessageByValue = {
  [EXPERIENCE_DESTINATION.MELBOURNE]: experienceDestinationMessages.melbourne,
  [EXPERIENCE_DESTINATION.SYDNEY]: experienceDestinationMessages.sydney,
  [EXPERIENCE_DESTINATION.BRISBANE]: experienceDestinationMessages.brisbane,
  [EXPERIENCE_DESTINATION.GOLD_COAST]: experienceDestinationMessages.goldCoast,
  [EXPERIENCE_DESTINATION.ADELAIDE]: experienceDestinationMessages.adelaide,
  [EXPERIENCE_DESTINATION.PERTH]: experienceDestinationMessages.perth,
  [EXPERIENCE_DESTINATION.THAILAND]: experienceDestinationMessages.thailand,
  [EXPERIENCE_DESTINATION.BALI]: experienceDestinationMessages.bali,
  [EXPERIENCE_DESTINATION.ANYWHERE]: experienceDestinationMessages.anywhere,
  [EXPERIENCE_DESTINATION.NEW_ZEALAND]: experienceDestinationMessages.newZealand,
  [EXPERIENCE_DESTINATION.FIJI]: experienceDestinationMessages.fiji,
  [EXPERIENCE_DESTINATION.AUCKLAND]: experienceDestinationMessages.auckland,
  [EXPERIENCE_DESTINATION.CHRISTCHURCH]: experienceDestinationMessages.christchurch,
  [EXPERIENCE_DESTINATION.QUEENSTOWN]: experienceDestinationMessages.queenstown,
} as const satisfies Record<ExperienceDestination, (typeof experienceDestinationMessages)[keyof typeof experienceDestinationMessages]>

export function formatExperienceDestinationLabel(intl: IntlShape, destination?: string): string | undefined {
  if (!destination) return undefined

  const message = experienceDestinationMessageByValue[destination]
  return message ? intl.formatMessage(message) : destination
}

export const experienceAnythingCategoryMessage = defineMessage({
  id: 'taLEuAoVndbd3WTtRHuy',
  defaultMessage: 'Anything',
  description: 'Experience search category label for the catch-all option that includes every experience category',
})

export const EXPERIENCE_ANYTHING_CATEGORY_NAME = experienceAnythingCategoryMessage.defaultMessage

export function formatExperienceAnythingCategoryLabel(intl: IntlShape): string {
  return intl.formatMessage(experienceAnythingCategoryMessage)
}

export type TransferType = 'AIRPORT-TO-HOTEL' | 'HOTEL-TO-AIRPORT' | 'ROUND-TRIP'

export const experienceTransferTripTypeMessages = defineMessages({
  roundTrip: {
    id: 'PWPccDS9E7mVtP5dRSU4',
    defaultMessage: 'Round trip',
    description: 'Transfer type label for round trip airport transfers shown on the my escapes bookings page',
  },
  airportToHotel: {
    id: 'ipJNqpvqZxKRtnI79Nr7',
    defaultMessage: 'Airport to Hotel',
    description: 'Transfer type label for airport to hotel transfers shown on the my escapes bookings page',
  },
  hotelToAirport: {
    id: 'CAJUrRNUrsBHLXYwetL0',
    defaultMessage: 'Hotel to Airport',
    description: 'Transfer type label for hotel to airport transfers shown on the my escapes bookings page',
  },
})

export const EXPERIENCE_TRANSFER_TRIP_TYPE = {
  ROUND_TRIP: experienceTransferTripTypeMessages.roundTrip.defaultMessage,
  AIRPORT_TO_HOTEL: experienceTransferTripTypeMessages.airportToHotel.defaultMessage,
  HOTEL_TO_AIRPORT: experienceTransferTripTypeMessages.hotelToAirport.defaultMessage,
} as const

export type TransferTripTypeDisplayText = typeof EXPERIENCE_TRANSFER_TRIP_TYPE[keyof typeof EXPERIENCE_TRANSFER_TRIP_TYPE]

const experienceTransferTripTypeMessageByTransferType = {
  'ROUND-TRIP': experienceTransferTripTypeMessages.roundTrip,
  'AIRPORT-TO-HOTEL': experienceTransferTripTypeMessages.airportToHotel,
  'HOTEL-TO-AIRPORT': experienceTransferTripTypeMessages.hotelToAirport,
} as const satisfies Record<TransferType, (typeof experienceTransferTripTypeMessages)[keyof typeof experienceTransferTripTypeMessages]>

export function formatExperienceTransferTripTypeLabel(intl: IntlShape, transferType: TransferType): TransferTripTypeDisplayText {
  return intl.formatMessage(experienceTransferTripTypeMessageByTransferType[transferType])
}

export interface ExperienceBrandDestination {
  id: string
  destinationKey: ExperienceDestination
}

function experienceBrandDestination(id: string, destinationKey: ExperienceDestination): ExperienceBrandDestination {
  return { id, destinationKey }
}

const EXPERIENCE_WL_DESTINATIONS: Array<ExperienceBrandDestination> = [
  experienceBrandDestination(Places.Melbourne.id, EXPERIENCE_DESTINATION.MELBOURNE),
  experienceBrandDestination(Places.Sydney.id, EXPERIENCE_DESTINATION.SYDNEY),
  experienceBrandDestination(Places.Brisbane.id, EXPERIENCE_DESTINATION.BRISBANE),
  experienceBrandDestination(Places.GoldCoast.id, EXPERIENCE_DESTINATION.GOLD_COAST),
  experienceBrandDestination(Places.Adelaide.id, EXPERIENCE_DESTINATION.ADELAIDE),
  experienceBrandDestination(Places.Perth.id, EXPERIENCE_DESTINATION.PERTH),
  experienceBrandDestination(Places.Thailand.id, EXPERIENCE_DESTINATION.THAILAND),
  experienceBrandDestination(Places.Bali.id, EXPERIENCE_DESTINATION.BALI),
  experienceBrandDestination(ANYWHERE_PLACE_ID, EXPERIENCE_DESTINATION.ANYWHERE),
]
const EXPERIENCE_LE_DESTINATIONS: Array<ExperienceBrandDestination> = [
  experienceBrandDestination(Places.Melbourne.id, EXPERIENCE_DESTINATION.MELBOURNE),
  experienceBrandDestination(Places.NewZealand.id, EXPERIENCE_DESTINATION.NEW_ZEALAND),
  experienceBrandDestination(Places.Fiji.id, EXPERIENCE_DESTINATION.FIJI),
  experienceBrandDestination(Places.Sydney.id, EXPERIENCE_DESTINATION.SYDNEY),
  experienceBrandDestination(Places.Bali.id, EXPERIENCE_DESTINATION.BALI),
  experienceBrandDestination(Places.Thailand.id, EXPERIENCE_DESTINATION.THAILAND),
  experienceBrandDestination(Places.GoldCoast.id, EXPERIENCE_DESTINATION.GOLD_COAST),
  experienceBrandDestination(Places.Perth.id, EXPERIENCE_DESTINATION.PERTH),
  experienceBrandDestination(ANYWHERE_PLACE_ID, EXPERIENCE_DESTINATION.ANYWHERE),
]

export const destinationsMap = switchFunc<Array<ExperienceBrandDestination>, App.BrandName>({
  luxuryescapes: EXPERIENCE_LE_DESTINATIONS,
  treatmetravel: [
    experienceBrandDestination(Places.Auckland.id, EXPERIENCE_DESTINATION.AUCKLAND),
    experienceBrandDestination(Places.Christchurch.id, EXPERIENCE_DESTINATION.CHRISTCHURCH),
    experienceBrandDestination(Places.Queenstown.id, EXPERIENCE_DESTINATION.QUEENSTOWN),
    experienceBrandDestination(Places.NewZealand.id, EXPERIENCE_DESTINATION.NEW_ZEALAND),
    experienceBrandDestination(Places.Sydney.id, EXPERIENCE_DESTINATION.SYDNEY),
    experienceBrandDestination(Places.Melbourne.id, EXPERIENCE_DESTINATION.MELBOURNE),
    experienceBrandDestination(Places.Brisbane.id, EXPERIENCE_DESTINATION.BRISBANE),
    experienceBrandDestination(ANYWHERE_PLACE_ID, EXPERIENCE_DESTINATION.ANYWHERE),
  ],
},
EXPERIENCE_WL_DESTINATIONS)

export const EXPERIENCE_SEARCH_TYPES: Array<App.SearchType> = ['destination', 'experience']

export const EXPERIENCE_TYPEAHEAD_SEARCH_TYPES: Array<App.SearchPlaceType> = [
  'province_state',
  'neighborhood',
  'city',
  'multi_city_vicinity',
  'high_level_region',
  'country',
  'continent',
  'colloquial_area',
  'le_experience',
  'channel_experience',
]

export const AnythingCategory: App.ExperienceItemCategory = {
  id: 999,
  imageId: '',
  level: 'parent',
  name: EXPERIENCE_ANYTHING_CATEGORY_NAME,
}

export enum ExperienceBookingType {
  BUY_NOW_BOOK_LATER = 'buy_now_book_later',
  INSTANT_BOOKING = 'instant_booking',
}

export const EXP_CATEGORY_FOOD_AND_DRINK_ID = 1
export const EXP_CATEGORY_TICKETS_AND_PASSES_ID = 18
export const EXP_CATEGORY_SHOWS_AND_EVENTS_ID = 27
export const EXP_CATEGORY_WELLNESS_AND_SPA_ID = 31
export const EXP_CATEGORY_DAY_TRIPS_ID = 35
export const EXP_CATEGORY_AIRPORT_TRANSFER_ID = 36
export const TRANSFER_SEARCH_CATEGORY_IDS = [36, 54, 37, 55, 47] as const
export const TRANSFER_SEARCH_VERTICAL = 'transfer'
export const EXP_CATEGORY_AIRPORT_LOUNGE_ID = 46
export const EXP_CATEGORY_GUIDED_TOURS_ID = 48

// Hotel/tour/cruise pins are not co-located with airports. 80km reaches city
// airports such as NRT (~58km from central Tokyo) while keeping the tighter
// 10km radius around actual flight airports.
export const LOUNGE_PASS_ACCOMMODATION_DISTANCE_RANGE: [string, string] = ['0KM', '80KM']

// Collinson (cln) lounge experience IDs for AU airports.
// These are unlisted in svc-experiences and absent from the search index,
// so they must be fetched explicitly via experienceIds + showUnlisted: true.
export const COLLINSON_AU_LOUNGE_IDS = ['cln:ADL', 'cln:BNE', 'cln:CNS', 'cln:MEL', 'cln:PER', 'cln:SYD']

// NZ lounges use the same explicit fetch path.
export const COLLINSON_NZ_LOUNGE_IDS = ['cln:AKL']

// Aspire (asp) lounge experience IDs. Unlisted and absent from the search index,
// must be included explicitly alongside Collinson IDs in the loyalty lounge fetch.
export const ASPIRE_LOUNGE_IDS = ['asp:164', 'asp:207', 'asp:208']

export const EXP_CATEGORY_WATER_TRANSFER_ID = 55
export const EXP_CATEGORY_PRIVATE_TRANSFER_ID = 37
export const EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID = 43
export const EXP_CATEGORY_LUX_PLUS_ID = 66
export const EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID = 67
export const EXP_CATEGORY_GIFT_INSPIRATION = 56
export const EXP_CATEGORY_LUX_EXCLUSIVE_ID = 40
export const EXP_CATEGORY_TOP_ACTIVITIES_ID = 9

export const EXPERIENCE_IN_FLOW_MAX_SHOWN = 12

export const TOP_ATTRACTIONS_SKELETON_COUNT = 10

export const EmptyExperienceList: NonNullable<App.ExperienceListState['']> = {
  ids: [],
  total: 0,
  fetching: true,
}

export const PROVIDERS: Record<string, string> = {
  led: 'led',
  rez: 'rezdy',
  dby: 'derbysoft',
  klo: 'klook',
  asp: 'aspire',
  cln: 'collinson',
  cmc: 'customlinc',
  hby: 'hubby',
  tfz: 'transferz',
}

// Disney California Adventure offer IDs (all include both Disneyland and Disney California Adventure parks)
export const DISNEY_CALIFORNIA_OFFER_IDS = [
  'dby:disneyland',
  'dby:disneyland-aunz-exclusive',
  'dby:176e6b74-1611-42a8-80f7-964d8f3ed857',
  'dby:05e08d61-7750-47a7-958d-13d9a27f2f3b',
  'dby:disneyland-hopper', // deprecated but may still exist in data
] as const

// Disney Orlando (Walt Disney World) offer ID
export const DISNEY_ORLANDO_OFFER_ID = 'dby:walt-disney-world' as const

/**
 * Maps carousel analytics names (from BigQuery / admin portal) to the heading
 * text rendered on the Experiences landing page. Used by the ?carousel= query
 * param to scroll to the matching carousel section.
 */
const CAROUSEL_HEADING_MESSAGES: Record<string, MessageDescriptor> = defineMessages({
  'OfferList - EXPERIENCES - Recently Viewed Carousel': {
    id: 'SufF1bNRPBtpVzsPvQP3',
    defaultMessage: 'Recently viewed',
    description: 'Heading on the experiences landing page for the recently viewed carousel',
  },
  'OfferList - EXPERIENCES - Experiences Top Three Carousel': {
    id: 'kU80Wygwc1y2nK_Q8mb3',
    defaultMessage: 'Our top 3 experiences',
    description: 'Heading on the experiences landing page for the top 3 experiences carousel',
  },
  'OfferList - EXPERIENCES - YAML Carousel': {
    id: 'SIHGvME39WB-yMUGoacP',
    defaultMessage: 'Local discounts exclusive to LuxPlus+ members',
    description: 'Heading on the experiences landing page for the LuxPlus member discounts carousel',
  },
  'OfferList - EXPERIENCES - Experiences Our exclusive experiences Carousel': {
    id: 'fjCaG1WYj3DXYbrFcT4u',
    defaultMessage: 'Our exclusive experiences',
    description: 'Heading on the experiences landing page for the exclusive experiences carousel',
  },
  'OfferList - EXPERIENCES - Experiences Top activities for you Carousel': {
    id: '744d2YY9wxRQOIbmuCnl',
    defaultMessage: 'Top activities for you',
    description: 'Heading on the experiences landing page for the top activities carousel',
  },
  'OfferList - EXPERIENCES - Experiences Discover dining experiences Carousel': {
    id: 'o3ZdvjQRXv_xr4Cfb01h',
    defaultMessage: 'Discover dining experiences',
    description: 'Heading on the experiences landing page for the dining experiences carousel',
  },
  'OfferList - EXPERIENCES - Experiences Explore your destination Carousel': {
    id: 'R3NM_Aiwsc6GnFJwOk2y',
    defaultMessage: 'Explore your destination',
    description: 'Heading on the experiences landing page for the destination exploration carousel',
  },
  'OfferList - EXPERIENCES - Collections Carousel - Summer Highlights': {
    id: 'ccWV9LiqzqZM5BKtFFQz',
    defaultMessage: 'Summer Highlights',
    description: 'Heading on the experiences landing page for the summer highlights collection carousel',
  },
  'OfferList - EXPERIENCES - Collections Carousel - Things to Do with Kids': {
    id: '390vrwZckHPdfus743W5',
    defaultMessage: 'Things to Do with Kids',
    description: 'Heading on the experiences landing page for the family activities collection carousel',
  },
  'OfferList - EXPERIENCES - Collections Carousel - Romantic Experiences': {
    id: 'YTT3iFfDc9ToSNoGTUMj',
    defaultMessage: 'Romantic Experiences',
    description: 'Heading on the experiences landing page for the romantic experiences collection carousel',
  },
  'OfferList - EXPERIENCES - Collections Carousel - Perfect Gifts for Someone Special': {
    id: 'rQTU8-kdmt9shaLop9Ya',
    defaultMessage: 'Perfect Gifts for Someone Special',
    description: 'Heading on the experiences landing page for the gift ideas collection carousel',
  },
  'OfferList - EXPERIENCES - Experiences See the sights Carousel': {
    id: 'bcJhP5MM5AFW7ItaA4Bv',
    defaultMessage: 'See the sights',
    description: 'Heading on the experiences landing page for the sightseeing carousel',
  },
  'OfferList - EXPERIENCES - Experiences Live events Carousel': {
    id: 'Wkz_NoBvG1EpkopBUiMq',
    defaultMessage: 'Live events',
    description: 'Heading on the experiences landing page for the live events carousel',
  },
  'OfferList - EXPERIENCES - Experiences Indulge yourself Carousel': {
    id: 'KZm2J__9qkixFEQPZ0Q5',
    defaultMessage: 'Indulge yourself',
    description: 'Heading on the experiences landing page for the indulgence carousel',
  },
})

export function getExperienceCarouselHeadingText(intl: IntlShape, carouselName: string): string | undefined {
  const headingMessage = CAROUSEL_HEADING_MESSAGES[carouselName]

  if (!headingMessage) {
    return undefined
  }

  return intl.formatMessage(headingMessage)
}

export interface BaliHotelLocation {
  name: string
  area: string
  zone: number
  placeId: string
}

export interface BaliHotelLocationsResponse {
  hotels: Array<BaliHotelLocation>
}

export const TRAVEL_STYLE_TO_TARGET_AUDIENCE: Record<Experiences.TravelStyleFilter, App.ExperienceTargetAudience> = {
  'family-friendly': 'FAMILY',
  'couples': 'COUPLE',
  'large-groups': 'GROUP',
  'solo-explorers': 'SOLO',
}

export const TARGET_AUDIENCE_TO_TRAVEL_STYLE: Record<App.ExperienceTargetAudience, Experiences.TravelStyleFilter> = {
  FAMILY: 'family-friendly',
  COUPLE: 'couples',
  GROUP: 'large-groups',
  SOLO: 'solo-explorers',
}

// Discover Near is shown only when the geo-resolved city has a curated destination list.

export type DiscoverNearDestinationConfig = {
  placeId: string
  imageId: string
  type?: App.Place['type']
  countryCode?: string
}

export const DISCOVER_NEAR_CURATED_CITIES: Record<string, Array<DiscoverNearDestinationConfig>> = {
  [Places.Sydney.id]: [
    { placeId: Places.HunterValley.id, imageId: 'ihte9r97djfq39f6268f', type: Places.HunterValley.type, countryCode: Places.HunterValley.countryCode },
    { placeId: 'le_b816159b242ae0f8a2035d213ee84f2a', imageId: 'pkewqtom69m4kuz5f0w8', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_5186b5eb83798a2a7a0bec02e7e95c5d', imageId: 'b8twufmpzjydwnwpqbu8', type: 'high_level_region', countryCode: 'AU' },
    { placeId: Places.BlueMountains.id, imageId: 'wq367xurmps0gh7oc5z', type: Places.BlueMountains.type, countryCode: Places.BlueMountains.countryCode },
    { placeId: 'le_c256df8aa62d7d94ff1a7c4ecf70245f', imageId: 'j22pku966j9patorre7k', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_8ebf84a4954068cc2a998f04fb26fea6', imageId: '4zdi3304yk2063xqykx', type: 'high_level_region', countryCode: 'AU' },
  ],
  [Places.Melbourne.id]: [
    { placeId: 'le_77a4a0e5a3bdfee80c9eff1420165977', imageId: '0uxv2l50olzc40675kb1zg', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_987b75e2727ae55289abd70d3f5864e6', imageId: '333pux7kqv0l01yxjy9', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_6e9ec3a5f04df981371602cee0f28e18', imageId: '4i20yq604acfx3p3npmo', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_570a90174cbbe07bda17a54f94f47637', imageId: 'a9uvcw4ly9jx2vzbyd6q', type: 'high_level_region', countryCode: 'AU' },
    { placeId: Places.YarraValley.id, imageId: 'wpek9s9p3qy0mmv854cm', type: Places.YarraValley.type, countryCode: Places.YarraValley.countryCode },
    { placeId: 'le_4e8a0fcae374fd3909f64555cdea95a1', imageId: 'rs3fqe3nzim6tuhg80go', type: 'city', countryCode: 'AU' },
  ],
  [Places.Brisbane.id]: [
    { placeId: Places.GoldCoast.id, imageId: '9m540szn9ucrq6r80v0e', type: Places.GoldCoast.type, countryCode: Places.GoldCoast.countryCode },
    { placeId: Places.SunshineCoast.id, imageId: 'k483p34am8keog4grimo', type: Places.SunshineCoast.type, countryCode: Places.SunshineCoast.countryCode },
    { placeId: 'le_e14e1364b73cc4878fe29890a4c29b7e', imageId: 'y467e3lxla3momuwm7yb', type: 'city', countryCode: 'AU' },
    { placeId: 'le_3b8592097e6f88f678a8983da586251d', imageId: 'qcmqragsolukwylvn7s5', type: 'city', countryCode: 'AU' },
    { placeId: 'le_c01ca642daebb0b11e22f9b92dafa065', imageId: 'jt2o5ssakijlt04yqt', type: 'city', countryCode: 'AU' },
    { placeId: 'le_7c765e60c6333f148ed2b70b3c09e6f2', imageId: '0nz4rvd1ket6dynahxwp', type: 'high_level_region', countryCode: 'AU' },
  ],
  [Places.Perth.id]: [
    { placeId: 'le_bb5c3a7f993b7cb25b2410e212e0386a', imageId: 'v6asjbz0acah9buv5w3', type: 'city', countryCode: 'AU' },
    { placeId: 'le_e4f9f1a6a6193f7100fc69a9abe3fc27', imageId: 'gu84st20z7awyl3w87in', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_3aaa3db6a8983226601cac5dde15a26b', imageId: 'mfjwoi6isq4sw6ges3nk', type: 'city', countryCode: 'AU' },
    { placeId: 'le_8e489b4966fe8f703b5be647f1cbae63', imageId: 'uzjximya3ppx40yfk3hv', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_589de5911c01c3c6735aa7acfbbb9336', imageId: 'oncfhpfwp2wyz19tx99', type: 'city', countryCode: 'AU' },
    { placeId: 'le_965b8da37b24c7a3d6377457ae238edc', imageId: '6gs1uk7i6hsz5i0ud84', type: 'high_level_region', countryCode: 'AU' },
  ],
  [Places.Adelaide.id]: [
    { placeId: 'le_94e7bf484204efe6fbcfc3db9d4ede83', imageId: 'wyuhpso20k1fglt7g6h', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_9819bda47e4a8234d65c1385a8a9e0ad', imageId: 'k40u0jpmvk0qbf0ksrc', type: 'high_level_region', countryCode: 'AU' },
    { placeId: Places.BarossaValley.id, imageId: 'qdefwo7wui139qcfifu54l', type: Places.BarossaValley.type, countryCode: Places.BarossaValley.countryCode },
    { placeId: 'le_4d3ad2b85e5fa9b30b87b0a482a08a58', imageId: '26keqmq6r52rokng0zkti', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_d66949480296c2957d4541919879f361', imageId: 'eouy55h5ngoz3ycnzaxe', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_b0297b4d47fd2943caea1bd65972be3b', imageId: 'kp6mtqwszda7hpu3uizw', type: 'high_level_region', countryCode: 'AU' },
  ],
  [Places.Hobart.id]: [
    { placeId: 'le_019cf615e71dc6ec778f64fb0022f3d9', imageId: 'a9t52tdnlcn5hg1hvrj', type: 'city', countryCode: 'AU' },
    { placeId: 'le_41b38946f3095d7ae9051db96fac0e89', imageId: 'v7pcl5lrh86kgbuhbjfa', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_de11adb2f1ce533eb0ddc6d763629151', imageId: 'wrglhxwkiasde494r8fh', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_05dbbf7d357cb071fe2ebe029438aab0', imageId: '7577z17rwiq8zo9y8k', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_0c0337840d91efd7f66c267c7567d324', imageId: '7w39benlu9gr331ncgx8', type: 'high_level_region', countryCode: 'AU' },
    { placeId: 'le_f2885d9caf005442fb9b78b400330823', imageId: 'v7pnl3r3zifl64kahoze', type: 'high_level_region', countryCode: 'AU' },
  ],
}
